import React, { useState } from 'react';
import { cn } from '@/lib/utils';

interface ColorPickerProps {
  onColorSelect: (color: string) => void;
  type: 'text' | 'background';
  currentColor?: string;
}

const textColors = {
  light: [
    { name: 'Default', value: '#000000' },
    { name: 'Gray', value: '#6B7280' },
    { name: 'Red', value: '#DC2626' },
    { name: 'Orange', value: '#EA580C' },
    { name: 'Yellow', value: '#D97706' },
    { name: 'Green', value: '#059669' },
    { name: 'Blue', value: '#2563EB' },
    { name: 'Purple', value: '#7C3AED' },
    { name: 'Pink', value: '#DB2777' },
  ],
  dark: [
    { name: 'Default', value: '#FFFFFF' },
    { name: 'Gray', value: '#9CA3AF' },
    { name: 'Red', value: '#F87171' },
    { name: 'Orange', value: '#FB923C' },
    { name: 'Yellow', value: '#FBBF24' },
    { name: 'Green', value: '#34D399' },
    { name: 'Blue', value: '#60A5FA' },
    { name: 'Purple', value: '#A78BFA' },
    { name: 'Pink', value: '#F472B6' },
  ]
};

const backgroundColors = {
  light: [
    { name: 'None', value: 'transparent' },
    { name: 'Yellow', value: '#FEF3C7' },
    { name: 'Green', value: '#D1FAE5' },
    { name: 'Blue', value: '#DBEAFE' },
    { name: 'Purple', value: '#E9D5FF' },
    { name: 'Pink', value: '#FCE7F3' },
    { name: 'Red', value: '#FEE2E2' },
    { name: 'Orange', value: '#FED7AA' },
    { name: 'Gray', value: '#F3F4F6' },
  ],
  dark: [
    { name: 'None', value: 'transparent' },
    { name: 'Yellow', value: '#92400E' },
    { name: 'Green', value: '#059669' },
    { name: 'Blue', value: '#1E40AF' },
    { name: 'Purple', value: '#6B21A8' },
    { name: 'Pink', value: '#BE185D' },
    { name: 'Red', value: '#B91C1C' },
    { name: 'Orange', value: '#C2410C' },
    { name: 'Gray', value: '#4B5563' },
  ]
};

export function ColorPicker({ onColorSelect, type, currentColor }: ColorPickerProps) {
  // Create modified light text colors (same as light background but replace transparent with white)
  const lightTextColors = type === 'text' 
    ? backgroundColors.light.map(color => 
        color.value === 'transparent' 
          ? { name: 'White', value: '#FFFFFF' }
          : color
      )
    : backgroundColors.light;

  const renderColorGrid = (colors: typeof lightTextColors, title: string) => (
    <div className="flex-1">
      <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
        {title}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {colors.map((color) => (
          <button
            key={`${title}-${color.value}`}
            onClick={() => onColorSelect(color.value)}
            className={cn(
              "h-7 w-full rounded border-2 transition-all hover:scale-105",
              currentColor === color.value 
                ? "border-blue-500 ring-2 ring-blue-200" 
                : "border-gray-300 dark:border-gray-600"
            )}
            style={{ 
              backgroundColor: color.value === 'transparent' ? '#ffffff' : color.value,
              backgroundImage: color.value === 'transparent' 
                ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                : undefined,
              backgroundSize: color.value === 'transparent' ? '6px 6px' : undefined,
              backgroundPosition: color.value === 'transparent' ? '0 0, 0 3px, 3px -3px, -3px 0px' : undefined
            }}
            title={color.name}
          >
            {color.value === 'transparent' && (
              <span className="text-xs text-red-500 font-bold">×</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-3 w-80">
      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
        {type === 'text' ? 'Text Color' : 'Background Color'}
      </div>
      
      <div className="flex gap-4">
        {renderColorGrid(
          lightTextColors, 
          type === 'text' ? 'Light Text' : 'Light Background'
        )}
        {renderColorGrid(
          type === 'text' ? textColors.light : textColors.light, 
          type === 'text' ? 'Dark Text' : 'Dark Background'
        )}
      </div>
    </div>
  );
} 